/**
 * Storyboard §66 — главный рабочий экран продукта.
 *
 * Список карточек сцен, без timeline-редактора (§20, §80).
 * Панель стоимости §36 закреплена: пользователь всегда видит, во что обойдётся
 * запуск, до того как нажмёт.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, RefreshCw } from 'lucide-react';
import { SceneType, pluralForms, withPlural } from '@alterium/shared';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { SceneCard } from '../../components/storyboard/SceneCard';
import { ScenePanel } from '../../components/storyboard/ScenePanel';
import { RenderPanel } from '../../components/storyboard/RenderPanel';
import { BreakdownPanel } from '../../components/storyboard/BreakdownPanel';
import { projectsApi } from '../../api/projects';
import type { SceneTraceRow } from '@alterium/shared';
import { looksApi } from '../../api/looks';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';
import { useAuthStore } from '../../stores/auth';

export function ProjectPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);

  const project = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id),
    refetchInterval: (query) =>
      query.state.data?.status === 'generating' || query.state.data?.status === 'directing'
        ? 4000
        : false,
    refetchOnWindowFocus: true,
  });

  const estimate = useQuery({
    queryKey: ['project-estimate', id, project.data?.updatedAt],
    queryFn: () => projectsApi.estimate(id),
    enabled: Boolean(project.data),
  });

  // §31: состояние сборки живёт отдельно от проекта — она идёт своей джобой
  // и обновляется чаще, чем раскадровка.
  const render = useQuery({
    queryKey: ['project-render', id, project.data?.updatedAt],
    queryFn: () => projectsApi.getRender(id),
    enabled: Boolean(project.data),
    refetchInterval: (query) =>
      query.state.data?.status === 'rendering' || query.state.data?.status === 'queued'
        ? 3000
        : false,
    // Сборка идёт минутами. Пока вкладка в фоне, опрос браузером приостановлен —
    // без обновления по возвращению пользователь увидел бы застывший прогресс
    // на уже собранном ролике.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['project', id] }),
      queryClient.invalidateQueries({ queryKey: ['project-estimate', id] }),
      queryClient.invalidateQueries({ queryKey: ['project-render', id] }),
    ]);

  const startRender = useMutation({ mutationFn: () => projectsApi.render(id), onSuccess: refresh });
  const stopRender = useMutation({
    mutationFn: () => projectsApi.cancelRender(id),
    onSuccess: refresh,
  });

  const download = useMutation({
    mutationFn: () => projectsApi.downloadUrl(id),
    // Ссылка живёт минуты: открываем сразу, а не храним в состоянии.
    onSuccess: (result) => window.open(result.url, '_blank', 'noopener'),
  });

  const updateScene = useMutation({
    mutationFn: async (params: {
      sceneId: string;
      patch: Record<string, unknown>;
      regenerate?: boolean;
    }) => {
      await projectsApi.updateScene(params.sceneId, params.patch);

      /**
       * Правка описания — это просьба пересоздать сцену (§36).
       *
       * Сохранение возвращает сцену в черновик, и раньше на этом всё
       * заканчивалось: человек переписывал промпт и не понимал, почему
       * ничего не происходит.
       */
      if (params.regenerate) await projectsApi.regenerateScene(params.sceneId);
    },
    onSuccess: async () => {
      setSelectedSceneId(null);
      await refresh();
    },
  });

  const duplicate = useMutation({ mutationFn: projectsApi.duplicateScene, onSuccess: refresh });
  const remove = useMutation({ mutationFn: projectsApi.deleteScene, onSuccess: refresh });
  const insert = useMutation({
    mutationFn: (afterSceneId: string | null) =>
      projectsApi.insertScene(id, afterSceneId, SceneType.BROLL),
    onSuccess: refresh,
  });
  const rebuild = useMutation({ mutationFn: () => projectsApi.rebuild(id), onSuccess: refresh });
  const generate = useMutation({ mutationFn: () => projectsApi.generate(id), onSuccess: refresh });
  const regenerate = useMutation({
    mutationFn: projectsApi.regenerateScene,
    onSuccess: refresh,
  });

  const looks = useQuery({ queryKey: ['looks'], queryFn: looksApi.list });

  /**
   * Чем сделана каждая сцена — только админу (§41).
   *
   * Имена внешних сервисов в пользовательской части не показываются нигде,
   * это критическое требование. Поэтому и запрос отдельный, и делается он
   * только для админа и служебных аккаунтов: у обычного пользователя таких
   * данных в ответе не появится даже случайно.
   */
  const canSeeTrace = useAuthStore(
    (state) => state.user?.role === 'admin' || state.user?.isService === true,
  );
  const trace = useQuery({
    queryKey: ['project-trace', id, project.data?.updatedAt],
    queryFn: () => projectsApi.trace(id),
    enabled: canSeeTrace,
  });
  const traceByScene = new Map((trace.data ?? []).map((row) => [row.sceneId, row]));

  const data = project.data;
  const selected = data?.scenes.find((scene) => scene.id === selectedSceneId) ?? null;

  // Пустой экран на ошибке — худшее из состояний: человек не понимает,
  // сломалось что-то или он не туда попал (§52).
  if (project.isError) {
    return (
      <div className="mx-auto w-full max-w-content px-6 py-10">
        <button
          type="button"
          onClick={() => navigate('/projects')}
          className="mb-6 flex items-center gap-1.5 text-meta text-content-muted transition-colors duration-fast hover:text-content"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
          {t('projects.title')}
        </button>
        <EmptyState
          title={t('project.notFound.title')}
          description={t('project.notFound.description')}
        />
      </div>
    );
  }

  if (!data) return <div className="min-h-[40vh]" />;

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/projects')}
        className="mb-4 flex items-center gap-1.5 text-meta text-content-secondary transition-colors duration-fast hover:text-content"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        {t('projects.title')}
      </button>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-page-title font-semibold">{data.title}</h1>
          <p className="mt-1 text-body text-content-secondary">
            {t(`enum.projectStatus.${data.status}`, { defaultValue: data.statusLabel })}
          </p>
        </div>
        <Button onClick={() => rebuild.mutate()} disabled={rebuild.isPending}>
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          {rebuild.isPending ? t('project.rebuilding') : t('project.rebuild')}
        </Button>
      </header>

      {rebuild.isError ? (
        <div className="mb-6">
          <Alert tone="error">{errorText(rebuild.error)}</Alert>
        </div>
      ) : null}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex-1">
          <div className="flex flex-col gap-3">
            {data.scenes.map((scene, index) => (
              <SceneCard
                key={scene.id}
                scene={scene}
                index={index}
                selected={scene.id === selectedSceneId}
                onSelect={() => setSelectedSceneId(scene.id)}
                onDuplicate={() => duplicate.mutate(scene.id)}
                onDelete={() => remove.mutate(scene.id)}
                onInsertAfter={() => insert.mutate(scene.id)}
                onRegenerate={() => regenerate.mutate(scene.id)}
                {...(traceByScene.get(scene.id)
                  ? { trace: traceByScene.get(scene.id) as SceneTraceRow }
                  : {})}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => insert.mutate(data.scenes.at(-1)?.id ?? null)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-card border border-dashed border-line py-3 text-meta text-content-muted transition-colors duration-fast hover:border-line-strong hover:text-content"
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            {t('project.addScene')}
          </button>
        </div>

        <div className="flex flex-col gap-4 lg:sticky lg:top-10 lg:w-[360px]">
          {selected ? (
            <ScenePanel
              key={selected.id}
              scene={selected}
              looks={looks.data ?? []}
              saving={updateScene.isPending}
              onSave={(patch, options) =>
                updateScene.mutate({
                  sceneId: selected.id,
                  patch,
                  ...(options?.regenerate ? { regenerate: true } : {}),
                })
              }
              onChanged={() => void refresh()}
              onClose={() => setSelectedSceneId(null)}
            />
          ) : (
            <>
              <CostPanel
                estimate={estimate.data}
                status={data.status}
                spentUnits={data.spentUnits}
                busy={generate.isPending}
                error={generate.isError ? errorText(generate.error) : null}
                onGenerate={() => generate.mutate()}
              />

              {/* §74: сборка появляется, когда есть что собирать. */}
              {render.data ? (
                <RenderPanel
                  render={render.data}
                  busy={startRender.isPending}
                  error={
                    startRender.isError
                      ? errorText(startRender.error)
                      : download.isError
                        ? errorText(download.error)
                        : null
                  }
                  onRender={() => startRender.mutate()}
                  onCancel={() => stopRender.mutate()}
                  onDownload={() => download.mutate()}
                />
              ) : null}

              {/* §20: из чего собран ролик — показываем, когда он готов. */}
              {render.data?.status === 'ready' ? (
                <BreakdownPanel projectId={id} version={project.data.updatedAt} />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Панель стоимости §36:
 *   7 сцен / 42 секунды / Создание видео 216 единиц / Ваш баланс 840 единиц
 *
 * Цена показывается ДО действия, а не после (§36): кнопка запуска и сумма
 * стоят рядом, чтобы решение принималось с полной информацией.
 */
function CostPanel({
  estimate,
  status,
  spentUnits,
  busy,
  error,
  onGenerate,
}: {
  estimate: ReturnType<typeof Object> | undefined;
  status: string;
  spentUnits: number;
  busy: boolean;
  error: string | null;
  onGenerate: () => void;
}) {
  const { t } = useTranslation();
  const data = estimate as
    | {
        sceneCount: number;
        totalDurationSec: number;
        totalUnits: number;
        available: number;
        enough: boolean;
        missing: number;
        lines: Array<{ operation: string; title: string; quantity: number; units: number }>;
      }
    | undefined;

  if (!data) return null;

  return (
    <Card>
      <CardBody className="flex flex-col gap-5">
        <div>
          <p className="text-body text-content">{withPlural(data.sceneCount, pluralForms.scene)}</p>
          <p className="text-body text-content-secondary">
            {withPlural(data.totalDurationSec, pluralForms.second)}
          </p>
        </div>

        <div className="flex flex-col gap-1.5 border-t border-line pt-4">
          {data.lines.map((line) => (
            <div key={line.operation} className="flex justify-between text-meta">
              <span className="text-content-secondary">{line.title}</span>
              <span className="text-content-muted">{line.units}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-line pt-4">
          <p className="text-meta text-content-secondary">{t('project.cost')}</p>
          <p className="text-section-title font-semibold text-content">
            {withPlural(data.totalUnits, pluralForms.unit)}
          </p>
          <p className="mt-2 text-meta text-content-secondary">{t('project.yourBalance')}</p>
          <p className="text-body text-content">{withPlural(data.available, pluralForms.unit)}</p>
        </div>

        {spentUnits > 0 ? (
          <p className="text-meta text-content-muted">
            {t('project.alreadySpent', { count: spentUnits })}
          </p>
        ) : null}

        {error ? <Alert tone="error">{error}</Alert> : null}

        {data.enough ? (
          <Button variant="primary" onClick={onGenerate} disabled={busy || status === 'generating'}>
            {busy || status === 'generating'
              ? t('project.generating')
              : status === 'partial'
                ? t('project.finish')
                : t('projects.createVideo')}
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <Alert tone="error">{t('project.notEnough', { count: data.missing })}</Alert>
            <Button variant="primary">{t('project.topUp')}</Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
